import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  PenNib, Plus, MagnifyingGlass, Article, PaperPlaneTilt, Archive,
  Trash, ArrowClockwise, ArrowRight, DotsThreeVertical, Eye, Clock,
} from "@phosphor-icons/react";

const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "draft", label: "Drafts" },
  { key: "pending", label: "Pending" },
  { key: "scheduled", label: "Scheduled" },
  { key: "approved", label: "Published" },
  { key: "archived", label: "Archived" },
  { key: "trashed", label: "Trash" },
];

const STATUS_STYLE = {
  draft: "bg-white border-black/15 text-[#3A3830]",
  pending: "bg-amber-50 border-amber-300 text-amber-800",
  scheduled: "bg-blue-50 border-blue-300 text-blue-800",
  approved: "bg-emerald-50 border-emerald-300 text-emerald-800",
  archived: "bg-black/5 border-black/15 text-[#3A3830]",
  trashed: "bg-red-50 border-red-300 text-red-800",
};

export default function PublisherDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setBusy(true);
    const params = { limit: 40 };
    if (status !== "all") params.status = status;
    if (q.trim()) params.q = q.trim();
    http.get("/publisher/posts", { params })
      .then(({ data }) => { setPosts(data.posts || []); setTotal(data.total || 0); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Load failed"))
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, status]);

  const runAction = async (post, action) => {
    try {
      await http.post(`/publisher/posts/${post.id}/${action}`);
      toast.success(`${action[0].toUpperCase()}${action.slice(1)}d`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  return (
    <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="publisher-dashboard">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-center gap-3 flex-wrap">
          <PenNib size={28} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
          <div className="flex-1 min-w-0">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">/ SocialPulse Publisher</div>
            <h1 className="mt-1 font-serif-display text-4xl md:text-5xl leading-[0.98]">My Posts</h1>
          </div>
          <Link
            to="/publisher/new"
            data-testid="publisher-new-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#151312] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase hover:opacity-90"
          >
            <Plus size={12} weight="bold" /> New post
          </Link>
        </div>

        {/* Search + tabs */}
        <div className="mt-8 rounded-2xl bg-white border border-black/10 p-4">
          <div className="flex items-center gap-2 rounded-full bg-white border border-black/15 px-3 py-2">
            <MagnifyingGlass size={14} weight="bold" className="text-[#3A3830]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search titles, tags…"
              data-testid="publisher-search"
              className="flex-1 bg-transparent outline-none text-sm"
            />
            <button onClick={load} className="rounded-full px-2 py-0.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]" data-testid="publisher-search-go">Go</button>
          </div>
          <div className="mt-3 flex gap-1.5 flex-wrap">
            {STATUS_TABS.map((s) => (
              <button
                key={s.key}
                onClick={() => setStatus(s.key)}
                data-testid={`publisher-tab-${s.key}`}
                className={`rounded-full px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase border ${status === s.key ? "border-transparent bg-[color:var(--np-brand,#B85F2A)] text-white" : "border-black/15 bg-white hover:bg-black/5"}`}
              >{s.label}</button>
            ))}
          </div>
        </div>

        {/* Posts list */}
        <div className="mt-6 space-y-2" data-testid="publisher-posts">
          {busy && Array.from({ length: 4 }).map((_, i) => <div key={i} className="rounded-xl bg-white/60 border border-black/10 h-24 animate-pulse" />)}
          {!busy && posts.length === 0 && (
            <div className="rounded-xl bg-white border border-black/10 p-8 text-center">
              <Article size={22} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)]" />
              <p className="mt-2 text-sm text-[#3A3830]">No posts yet. Start writing your first article.</p>
              <Link to="/publisher/new" className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#151312] text-[#F5EBDC] px-5 py-2 font-mono-tech text-[10px] tracking-[0.3em] uppercase">
                <Plus size={12} weight="bold" /> New post
              </Link>
            </div>
          )}
          {posts.map((p) => (
            <div key={p.id} data-testid={`publisher-post-${p.id}`} className="rounded-xl bg-white border border-black/10 p-4 flex gap-4 items-start hover:border-[color:var(--np-brand,#B85F2A)] transition">
              {(p.featured_image_url || p.media?.[0]?.url) ? (
                <img src={p.featured_image_url || p.media[0].url} alt="" className="w-24 h-24 object-cover rounded-lg flex-shrink-0" />
              ) : (
                <div className="w-24 h-24 rounded-lg bg-[#FBF2E1] grid place-items-center flex-shrink-0"><Article size={22} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" /></div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`rounded-full px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.24em] uppercase border ${STATUS_STYLE[p.moderation_status] || "border-black/15 bg-white text-[#3A3830]"}`}>{p.moderation_status || "draft"}</span>
                  {p.category && <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#3A3830]">{p.category}</span>}
                  {p.scheduled_at && <span className="inline-flex items-center gap-1 text-[10px] text-blue-800"><Clock size={10} weight="fill" /> {new Date(p.scheduled_at).toLocaleString()}</span>}
                </div>
                <h3 className="mt-1 font-serif-display text-lg leading-tight truncate">{p.title || p.headline || <span className="text-[#3A3830] italic">Untitled draft</span>}</h3>
                {p.excerpt && <p className="mt-1 text-xs text-[#3A3830] line-clamp-2">{p.excerpt}</p>}
                <div className="mt-2 flex items-center gap-3 text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#3A3830]">
                  {p.updated_at && <span>Updated {new Date(p.updated_at).toLocaleDateString()}</span>}
                  {p.short_url && <a href={p.short_url} target="_blank" rel="noreferrer noopener" className="text-[color:var(--np-brand,#B85F2A)] hover:underline" data-testid={`post-short-${p.id}`}>{p.short_url.replace(/^https?:\/\//, "")}</a>}
                </div>
              </div>
              <div className="flex flex-col gap-1.5 flex-shrink-0">
                <Link to={`/publisher/edit/${p.id}`} data-testid={`edit-${p.id}`} className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:bg-black/5">
                  <PenNib size={10} weight="fill" /> Edit
                </Link>
                {p.moderation_status === "approved" && (
                  <a href={`/stories/${p.id}`} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:bg-black/5" data-testid={`view-${p.id}`}>
                    <Eye size={10} weight="fill" /> View
                  </a>
                )}
                {p.moderation_status === "approved" && (
                  <button onClick={() => runAction(p, "unpublish")} data-testid={`unpub-${p.id}`} className="rounded-full border border-black/15 bg-white px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:bg-black/5">Unpublish</button>
                )}
                {p.moderation_status !== "trashed" && p.moderation_status !== "archived" && (
                  <button onClick={() => runAction(p, "archive")} data-testid={`archive-${p.id}`} className="rounded-full border border-black/15 bg-white px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:bg-black/5 inline-flex items-center gap-1"><Archive size={10} weight="fill" /> Archive</button>
                )}
                {p.moderation_status === "archived" && (
                  <button onClick={() => runAction(p, "restore")} data-testid={`restore-${p.id}`} className="rounded-full border border-black/15 bg-white px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:bg-black/5 inline-flex items-center gap-1"><ArrowClockwise size={10} weight="fill" /> Restore</button>
                )}
                <button onClick={() => runAction(p, "trash")} data-testid={`trash-${p.id}`} className="rounded-full border border-red-300 bg-white text-red-800 px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:bg-red-50 inline-flex items-center gap-1"><Trash size={10} weight="fill" /> Trash</button>
              </div>
            </div>
          ))}
        </div>
        {posts.length > 0 && (
          <div className="mt-4 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]">
            Showing {posts.length} of {total} posts
          </div>
        )}
      </div>
    </main>
  );
}
